// ---------------------------------------------------------------------------
// Zustand gegen Text und Zeit (Roadmap AP-42, PF 24; aus PF 23 der Auftritt vor der Geburt bzw. nach dem Tod):
//  - Lebt die Figur? Sie spricht, handelt, denkt oder erfährt etwas (Bezug im Draft), hat die Perspektive oder eine Änderung betrifft sie
//    (auch: bekommt oder gibt einen Gegenstand, stirbt noch einmal) – zu einer Zeit, zu der sie schon tot bzw. noch nicht geboren ist.
//  - Hat sie den Gegenstand? Wer ihn weitergibt bzw. verliert (`StateChange.from`), hat ihn zu dieser Zeit nicht: er ist bei jemand
//    anderem, an einem Ort oder verloren.
// Geburt und Tod kommen aus den Lebensdaten (lifespan.ts), der Besitz aus den Änderungen davor (states.ts). Verglichen wird in Weltzeit,
// wenn beide Szenen vergleichbare Zeiten haben, in der gröberen Genauigkeit beider (ungefähre nur aufs Jahr): dann ein Widerspruch.
// Sonst nach der Lesereihenfolge, wenn beide im Manuskript stehen – nur ein Hinweis, denn es kann eine Rückblende sein.
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { afterPriorEvent, lifeOf, type Life } from './lifespan.js';
import { narrativeOrder, povOf, worldSpanOf } from './narrative.js';
import { refIndex } from './refs.js';
import { priorOf } from './series.js';
import { changesOf, initialHolder, stateEvents, stateRoleOf, type StateEvent } from './states.js';
import { spansIn, type SpanRole } from './spans.js';
import { sameAt } from './textTime.js';
import { TIME_PRECISIONS, type Entity, type IgnoredTerm, type PriorSource, type StateChange, type StoryNode, type TimePrecision } from './types.js';

/** Wie sicher eine Reihenfolge ist: aus der Weltzeit bzw. nur aus der Lesereihenfolge */
export type Certainty = 'sure' | 'likely';

/** Genauigkeit einer Szene für den Vergleich: ungefähre nur aufs Jahr */
const precisionOf = (n: StoryNode): TimePrecision => (n.time?.approx ? 'year' : (n.time?.precision ?? 'minute'));
const coarser = (a: TimePrecision, b: TimePrecision): TimePrecision => TIME_PRECISIONS[Math.min(TIME_PRECISIONS.indexOf(a), TIME_PRECISIONS.indexOf(b))]!;

/**
 * Liegt Szene `a` vor Szene `b`? `sure`: in Weltzeit (endet vor dem Beginn von `b`, auch in der gröberen Genauigkeit beider); `likely`:
 * ohne vergleichbare Zeiten nach der Lesereihenfolge (`reading`: Stelle im Manuskript); sonst null (danach, gleichzeitig oder unklar).
 */
export function sceneBefore(ctx: StoryCtx, a: StoryNode, b: StoryNode, reading: ReadonlyMap<string, number>): Certainty | null {
  if (a.id === b.id) return null;
  const sa = worldSpanOf(ctx, a);
  const sb = worldSpanOf(ctx, b);
  if (sa && sb && sa.basis === sb.basis) {
    if (sa.end >= sb.start) return null;
    const basis = sa.basis === 'ms' ? 'ms' : sa.basis === 'count' ? 'count' : null;
    if (basis && sameAt({ basis, value: sa.end, unit: null }, { basis, value: sb.start, unit: null }, coarser(precisionOf(a), precisionOf(b)))) return null;
    return 'sure';
  }
  const ra = reading.get(a.id);
  const rb = reading.get(b.id);
  return ra !== undefined && rb !== undefined && ra < rb ? 'likely' : null;
}

/** Was die Prüfungen je Stand der Geschichte brauchen: Änderungen in Weltzeit, Stellen im Manuskript, Lebensdaten je Figur */
interface Env {
  all: Omit<StateEvent, 'role'>[];
  reading: Map<string, number>;
  lives: Map<string, Life>;
}
const envs = new WeakMap<StoryCtx, Env>();
function envOf(ctx: StoryCtx): Env {
  let env = envs.get(ctx);
  if (!env) envs.set(ctx, (env = { all: stateEvents(ctx), reading: new Map(narrativeOrder(ctx).scenes.map((s, i) => [s.scene.id, i])), lives: new Map() }));
  return env;
}
const lifeIn = (ctx: StoryCtx, env: Env, entityId: string): Life => {
  let life = env.lives.get(entityId);
  if (!life) env.lives.set(entityId, (life = lifeOf(ctx, entityId, env.all)));
  return life;
};

/**
 * Eine Figur, die zur Zeit einer Szene nicht lebt: schon tot bzw. noch nicht geboren, mit der Szene von Tod bzw. Geburt (leer, wenn sie
 * in einer früheren Episode der Serie starb: dann `prior`)
 */
export interface LifeConflict {
  entityId: string;
  state: 'dead' | 'unborn';
  eventSceneId: string;
  certainty: Certainty;
  prior?: PriorSource;
}

/** Lebt `entityId` zur Zeit von `scene` nicht? (ohne Lebensdaten: nichts) */
export function lifeConflict(ctx: StoryCtx, scene: StoryNode, entityId: string): LifeConflict | null {
  const env = envOf(ctx);
  const { born, died } = lifeIn(ctx, env, entityId);
  // gestorben in einer früheren Episode: tot, außer die Szene liegt in Weltzeit davor (Rückblende)
  if (died?.prior) {
    const after = afterPriorEvent(ctx, scene, died);
    return after ? { entityId, state: 'dead', eventSceneId: '', certainty: after, prior: died.prior } : null;
  }
  const death = died && ctx.nodes.get(died.sceneId);
  const afterDeath = death ? sceneBefore(ctx, death, scene, env.reading) : null;
  if (afterDeath) return { entityId, state: 'dead', eventSceneId: death!.id, certainty: afterDeath };
  const birth = born && !born.prior ? ctx.nodes.get(born.sceneId) : undefined;
  const beforeBirth = birth ? sceneBefore(ctx, scene, birth, env.reading) : null;
  return beforeBirth ? { entityId, state: 'unborn', eventSceneId: birth!.id, certainty: beforeBirth } : null;
}

/** Wo eine Figur in der Szene vorkommt, obwohl sie nicht lebt: im Text, als Perspektive, in einer Änderung */
export type LifeWhere = 'text' | 'perspektive' | 'änderung';

const keyOf = (ctx: StoryCtx, id: string): string => ctx.entities.get(id)?.refKey ?? id;

/** „Ist Absicht“ (ein Geist, eine Vision …): für diese Figur an dieser Stelle der Szene, gemerkt im Draft */
export const lifeIgnore = (ctx: StoryCtx, entityId: string, where: LifeWhere): IgnoredTerm => ({ term: '~lebt', before: `@${keyOf(ctx, entityId)}`, after: where });
const lifeIgnored = (ignored: readonly IgnoredTerm[] | undefined, entry: IgnoredTerm): boolean => !!ignored?.some((x) => x.term === entry.term && x.before === entry.before && x.after === entry.after);

/** Rollen eines Bezugs, in denen eine Figur lebt: sie spricht, handelt, denkt, erfährt etwas */
export const LIVING_ROLES: readonly SpanRole[] = ['rede', 'handlung', 'gedanke', 'erfährt'];

/** Die Personen eines Textes (Markdown), die darin sprechen, handeln, denken oder etwas erfahren – je Person die erste Stelle */
export function livingIn(ctx: StoryCtx, text: string, refs: ReadonlyMap<string, Entity> = refIndex(ctx)): { entityId: string; role: SpanRole; index: number }[] {
  const out = new Map<string, { entityId: string; role: SpanRole; index: number }>();
  for (const s of spansIn(text)) {
    if (!s.role || !LIVING_ROLES.includes(s.role)) continue;
    for (const k of s.keys) {
      const e = refs.get(k);
      if (e && stateRoleOf(ctx, e.id) === 'person' && !out.has(e.id)) out.set(e.id, { entityId: e.id, role: s.role, index: s.from });
    }
  }
  return [...out.values()];
}

/** Figuren, die im Draft der Szene leben, obwohl sie tot bzw. ungeboren sind (ohne „ist Absicht“) – für den Editor: je Figur */
export function lifeTextConflicts(ctx: StoryCtx, scene: StoryNode, text: string, ignored?: readonly IgnoredTerm[], refs?: ReadonlyMap<string, Entity>): (LifeConflict & { role: SpanRole; index: number })[] {
  if (!text.includes(']{@')) return [];
  return livingIn(ctx, text, refs).flatMap((l) => {
    const c = lifeConflict(ctx, scene, l.entityId);
    return c && !lifeIgnored(ignored, lifeIgnore(ctx, l.entityId, 'text')) ? [{ ...c, role: l.role, index: l.index }] : [];
  });
}

/** Die Perspektive der Szene lebt nicht */
export function lifePovConflict(ctx: StoryCtx, scene: StoryNode, ignored?: readonly IgnoredTerm[]): LifeConflict | null {
  const pov = povOf(ctx, scene);
  const c = pov ? lifeConflict(ctx, scene, pov) : null;
  return c && !lifeIgnored(ignored, lifeIgnore(ctx, c.entityId, 'perspektive')) ? c : null;
}

/** Änderungen der Szene an Figuren, die nicht leben (auch: bekommen bzw. geben einen Gegenstand) – je Figur die erste */
export function lifeChangeConflicts(ctx: StoryCtx, scene: StoryNode, ignored?: readonly IgnoredTerm[]): (LifeConflict & { change: StateChange })[] {
  const out = new Map<string, LifeConflict & { change: StateChange }>();
  for (const change of changesOf(scene)) {
    const who = [change.entityId, ...(change.kind === 'besitz' ? [change.value, change.from] : [])].filter((id): id is string => !!id && stateRoleOf(ctx, id) === 'person');
    for (const id of who) {
      if (out.has(id) || lifeIgnored(ignored, lifeIgnore(ctx, id, 'änderung'))) continue;
      const c = lifeConflict(ctx, scene, id);
      if (c) out.set(id, { ...c, change });
    }
  }
  return [...out.values()];
}

/**
 * Wer einen Gegenstand weitergibt bzw. verliert, hat ihn zu dieser Zeit nicht: er ist bei `holder` (null: verloren) seit `holderSceneId`
 * (leer: von Anfang an, laut Feld „Besitzer“ bzw. seit einer früheren Episode der Serie, dann `prior`)
 */
export interface HolderConflict {
  change: StateChange & { from: string };
  holder: string | null;
  holderSceneId: string;
  certainty: Certainty;
  prior?: PriorSource;
}

/** „Ist Absicht“ für einen Besitzwechsel: dieser Gegenstand von dieser Figur, gemerkt im Draft */
export const holderIgnore = (ctx: StoryCtx, c: Pick<StateChange, 'entityId'> & { from: string }): IgnoredTerm => ({ term: '~besitz', before: `@${keyOf(ctx, c.entityId)}`, after: `@${keyOf(ctx, c.from)}` });

/** Besitzwechsel der Szene von jemandem, der den Gegenstand da nicht hat; unbekannt, bei wem er davor war: nichts */
export function holderConflicts(ctx: StoryCtx, scene: StoryNode, ignored?: readonly IgnoredTerm[]): HolderConflict[] {
  const env = envOf(ctx);
  const out: HolderConflict[] = [];
  for (const change of changesOf(scene)) {
    if (change.kind !== 'besitz' || !change.from || lifeIgnored(ignored, holderIgnore(ctx, { entityId: change.entityId, from: change.from }))) continue;
    const at = env.all.findIndex((e) => e.change === change);
    // der letzte Besitzwechsel davor: in derselben Szene weiter oben, sonst in einer Szene, die davor liegt
    let prev: { e: Omit<StateEvent, 'role'>; certainty: Certainty } | null = null;
    for (let i = 0; i < env.all.length; i++) {
      const e = env.all[i]!;
      if (e.change.kind !== 'besitz' || e.change.entityId !== change.entityId || e.change === change) continue;
      const before = e.sceneId === scene.id ? (i < at ? 'sure' : null) : sceneBefore(ctx, ctx.nodes.get(e.sceneId)!, scene, env.reading);
      if (before) prev = { e, certainty: before };
    }
    if (!prev) {
      // vorher kein Besitzwechsel: bei wem er am Ende der vorigen Episode war (Serie), sonst laut Feld „Besitzer“ zu Beginn (AP-31)
      const prior = priorOf(ctx, change.entityId)?.holder;
      if (prior) {
        if (prior.value !== change.from) out.push({ change: change as StateChange & { from: string }, holder: prior.value, holderSceneId: '', certainty: 'sure', prior: { episode: prior.episode, scene: prior.scene } });
        continue;
      }
      const initial = initialHolder(ctx, change.entityId);
      if (initial && initial !== change.from) out.push({ change: change as StateChange & { from: string }, holder: initial, holderSceneId: '', certainty: 'sure' });
      continue;
    }
    if (prev.e.change.value === change.from) continue;
    out.push({ change: change as StateChange & { from: string }, holder: prev.e.change.value, holderSceneId: prev.e.sceneId, certainty: prev.certainty });
  }
  return out;
}
